/**
 * Atlas PM · 进度计算（P6.41 模型重构）
 *
 * ⭐ 核心业务规则：**进度是算出来的，不是用户填的。**
 *
 *   【完成度链路】
 *     要素完成度 ∈ [0,100]（用户填）
 *     指标完成度 = Σ(要素占指标权重 × 要素完成度) / 100
 *     任务完成度 = Σ(指标权重 × 指标完成度) / 100
 *
 *   【质量链路（平行，互不影响）】
 *     要素质量   ∈ [0,100]（用户填）
 *     指标质量   = Σ(要素占指标权重 × 要素质量) / 100
 *     任务质量   = Σ(指标权重 × 指标质量) / 100
 *
 *   【约束】
 *     一个任务的所有指标权重之和 = 100
 *     一个指标的所有要素「占指标权重」之和 = 100
 *     一个指标至少 1 个要素
 *
 *   ⚠️ 返回值统一为 **0-100 的百分数**（不再是 0-1 比率）。
 *      不要再对该结果调用 `toPercent()`（那是给 0-1 比率用的），
 *      否则会出现 10000% 这种数字。
 *
 *   ⚠️ 边界：
 *     · 任务无指标            → 完成度 = 0，质量 = 0
 *     · 指标无要素            → 完成度 = 0，质量 = 0
 *     · 权重和 ≠ 100（历史脏数据）→ 按 Σ权重 归一化（防御），
 *       避免出现 >100% 的完成度；提交时另有强校验。
 *
 * 本文件是**纯函数**，前后端共用——保证两端口径永远一致。
 */
function clampPct(v) {
    if (!Number.isFinite(v))
        return 0;
    if (v < 0)
        return 0;
    if (v > 100)
        return 100;
    return v;
}
/**
 * 通用加权平均（0-100）。
 *
 * @param totalWeight 分母；≤0 时返回 0
 * @param weighted    分子（Σ 权重 × 分值）
 */
function weightedAvg(totalWeight, weighted) {
    if (totalWeight <= 0)
        return 0;
    const result = weighted / totalWeight;
    return clampPct(result);
}
/**
 * 计算单个指标的完成度（0-100）。
 *
 * 边界处理：
 * - 无要素 → 返回 0（"没定义完成标准"不应被视为已完成）
 * - 权重和 ≤ 0 → 返回 0
 * - 权重和 ≠ 100 → 按 Σ权重 归一化（防御历史脏数据）
 */
export function calcMetricCompletion(elements) {
    if (elements.length === 0)
        return 0;
    const totalWeight = elements.reduce((sum, e) => sum + (e.weight ?? 0), 0);
    const weighted = elements.reduce((sum, e) => sum + (e.weight ?? 0) * clampPct(e.completionPct ?? 0), 0);
    return weightedAvg(totalWeight, weighted);
}
/**
 * 计算单个指标的质量（0-100）。
 *
 * ⚠️ 与完成度**完全平行**：要素质量变化只影响质量链路，不影响完成度。
 * 边界同 `calcMetricCompletion`。
 */
export function calcMetricQuality(elements) {
    if (elements.length === 0)
        return 0;
    const totalWeight = elements.reduce((sum, e) => sum + (e.weight ?? 0), 0);
    const weighted = elements.reduce((sum, e) => sum + (e.weight ?? 0) * clampPct(e.qualityPct ?? 0), 0);
    return weightedAvg(totalWeight, weighted);
}
/**
 * 计算任务完成度（0-100）。
 *
 * 边界处理：
 * - 无指标 → 返回 0
 * - 权重和 ≤ 0 → 返回 0
 * - 权重和 ≠ 100 → 按 Σ权重 归一化（防御历史脏数据）
 */
export function calcTaskProgress(metrics) {
    if (metrics.length === 0)
        return 0;
    const totalWeight = metrics.reduce((sum, m) => sum + (m.weight ?? 0), 0);
    const weighted = metrics.reduce((sum, m) => sum + (m.weight ?? 0) * calcMetricCompletion(m.elements), 0);
    return weightedAvg(totalWeight, weighted);
}
/**
 * 计算任务质量（0-100）。
 *
 * ⚠️ 与任务完成度**完全平行**：要素质量变化只影响质量链路。
 * 边界同 `calcTaskProgress`。
 */
export function calcTaskQuality(metrics) {
    if (metrics.length === 0)
        return 0;
    const totalWeight = metrics.reduce((sum, m) => sum + (m.weight ?? 0), 0);
    const weighted = metrics.reduce((sum, m) => sum + (m.weight ?? 0) * calcMetricQuality(m.elements), 0);
    return weightedAvg(totalWeight, weighted);
}
/** 转百分比（保留一位小数）——⚠️ 仅用于 0-1 比率（产出物齐套率），不要用于进度函数返回值 */
export function toPercent(ratio) {
    return Math.round(ratio * 1000) / 10;
}
// ─────────────────────────────────────────────
// P6.41 权重校验（提交时强校验；编辑期允许≠100）
// ─────────────────────────────────────────────
/** 权重校验错误码（与后端 API 错误码一致） */
export const WEIGHT_ERROR = {
    METRIC_WEIGHT_SUM_INVALID: 'METRIC_WEIGHT_SUM_INVALID',
    ELEMENT_WEIGHT_SUM_INVALID: 'ELEMENT_WEIGHT_SUM_INVALID',
    METRIC_ELEMENT_REQUIRED: 'METRIC_ELEMENT_REQUIRED',
};
/** 浮点安全地判断“≈ 100”（权重为整数时应精确命中） */
function isHundred(n) {
    return Math.abs(n - 100) < 1e-9;
}
/**
 * 校验任务权重模型（前后端共用，保证错误口径一致）。
 *
 * - 每个指标至少 1 个要素
 * - 每个指标内要素「占指标权重」之和 = 100
 * - 任务内所有指标权重之和 = 100
 */
export function validateTaskWeights(metrics) {
    const issues = [];
    for (const m of metrics) {
        const ref = m.id != null ? String(m.id) : undefined;
        if (m.elements.length === 0) {
            issues.push({
                code: WEIGHT_ERROR.METRIC_ELEMENT_REQUIRED,
                message: '每个指标至少需要一个要素',
                ref,
            });
            continue;
        }
        const elSum = m.elements.reduce((s, e) => s + (e.weight ?? 0), 0);
        if (!isHundred(elSum)) {
            issues.push({
                code: WEIGHT_ERROR.ELEMENT_WEIGHT_SUM_INVALID,
                message: `指标「${ref ?? ''}」下要素权重之和为 ${elSum}，必须等于 100`,
                ref,
            });
        }
    }
    const metricSum = metrics.reduce((s, m) => s + (m.weight ?? 0), 0);
    if (!isHundred(metricSum)) {
        issues.push({
            code: WEIGHT_ERROR.METRIC_WEIGHT_SUM_INVALID,
            message: `指标权重之和为 ${metricSum}，必须等于 100`,
        });
    }
    return issues;
}
/** 健康度分档（与视觉规范一致，见方案文档 40-视觉与交互设计规范.md） */
export const HEALTH_LEVEL = {
    /** 健康 ≥ 85 */
    HEALTHY: 'HEALTHY',
    /** 预警 60 ~ 85 */
    WARNING: 'WARNING',
    /** 告警 < 60 */
    ALERT: 'ALERT',
};
/** 健康度对应的状态色（前端与地图共用） */
export const HEALTH_COLORS = {
    HEALTHY: '#54E0A8',
    WARNING: '#F0C060',
    ALERT: '#FF6B8A',
};
/** 按健康度分数取档 */
export function healthLevelOf(score) {
    if (score >= 85)
        return HEALTH_LEVEL.HEALTHY;
    if (score >= 60)
        return HEALTH_LEVEL.WARNING;
    return HEALTH_LEVEL.ALERT;
}
/** 由健康度取颜色 */
export function healthColorOf(score) {
    return HEALTH_COLORS[healthLevelOf(score)];
}
// ─────────────────────────────────────────────
// P6.52 项目健康度（由任务聚合四项加权算出）
// ─────────────────────────────────────────────
/**
 * 项目健康度权重。
 *
 * 依据方案总览「健康度：由下属任务的进度、逾期、指标达标率、审批积压四项加权算出」，
 * 四项归一化到 0-100 后加权求和。
 *
 *   progressScore = progress_pct                              （进度达标度）
 *   metricScore   = 指标达标率；无指标时取 100（无量化要求不惩罚）
 *   overdueScore  = (1 - 逾期数/任务数) × 100                 （逾期情况）
 *   approvalScore = (1 - min(1, 待审数/任务数)) × 100         （审批顺畅度）
 *
 * ⚠️ 无任务 → health = 0（无数据不臆造健康度，前端据此保持 IDLE 灰点）。
 */
export const PROJECT_HEALTH_WEIGHTS = {
    progress: 0.4,
    metricPassRate: 0.3,
    overdue: 0.15,
    approval: 0.15,
};
/**
 * 计算项目健康度 0-100（可解释、可复算；公式见 PROJECT_HEALTH_WEIGHTS 注释）。
 *
 * ⚠️ 这是**纯函数**：后端重算落库与前端展示可复用同一口径。
 */
export function calcProjectHealthScore(input) {
    const { taskCount, progressPct, metricPassRate, metricTotal, overdueTaskCnt, approvalBacklog } = input;
    if (taskCount <= 0)
        return 0;
    const progressScore = clampPct(progressPct);
    const metricScore = metricTotal > 0 ? clampPct(metricPassRate) : 100;
    const overdueScore = (1 - Math.min(1, overdueTaskCnt / taskCount)) * 100;
    const approvalScore = (1 - Math.min(1, approvalBacklog / taskCount)) * 100;
    const w = PROJECT_HEALTH_WEIGHTS;
    const score = w.progress * progressScore +
        w.metricPassRate * metricScore +
        w.overdue * overdueScore +
        w.approval * approvalScore;
    return Math.max(0, Math.min(100, Math.round(score)));
}
/**
 * 产出物齐套率（Deliverable Contract）。
 *
 *   齐套率 = 已交付数量 / 应交数量
 *
 * 边界：应交为 0 → 返回 1（没有约定就不缺）
 */
export function calcDeliverableCompleteness(required, delivered) {
    if (required <= 0)
        return 1;
    return Math.min(1, Math.max(0, delivered / required));
}
// ─────────────────────────────────────────────
// ⭐ P6.54 要素齐套率（P6.51 口径，任务级 / 项目级共用，避免两套算法）
// ─────────────────────────────────────────────
/**
 * 要素齐套率（%）。
 *
 *   齐套率 = 已上传至少 1 个有效附件的「需附件要素」数 / 全部「需附件要素」数 × 100
 *
 * ⚠️ 分母为 0（项目/任务下没有任何 requireAttachment=true 的要素）→ 返回 null，
 *    前端显示「—」而不是 0%（0% 会被误读为「全部缺交」）。
 * ⚠️ 任务级（DeliverableService.completeness）与项目级（ProjectService.completeness /
 *    ProjectAggregateService.recompute）必须共用本函数，保证口径永远一致。
 */
export function calcElementCompletenessPct(requiredTotal, requiredSatisfied) {
    if (requiredTotal <= 0)
        return null;
    const ratio = Math.min(1, Math.max(0, requiredSatisfied / requiredTotal));
    return Math.round(ratio * 1000) / 10;
}
//# sourceMappingURL=progress.js.map